/**
 * TikTok Click ID (ttclid) Management Utility
 * 
 * Handles capturing, storing, and retrieving ttclid from TikTok ad clicks
 * for proper conversion attribution within the attribution window.
 */

const TTCLID_KEY = 'ttclid';
const DEFAULT_ATTRIBUTION_DAYS = 7;

/**
 * Reads ttclid parameter from the current URL
 */
export function readTtclidFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('ttclid');
}

/**
 * Stores ttclid in a first-party cookie
 * @param value - The ttclid value to store
 * @param days - Number of days until cookie expires (matches attribution window)
 */
export function setTtclidCookie(value: string, days = DEFAULT_ATTRIBUTION_DAYS): void {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + days);
  
  document.cookie = `${TTCLID_KEY}=${encodeURIComponent(value)}; path=/; expires=${expirationDate.toUTCString()}; samesite=lax`;
}

/**
 * Retrieves stored ttclid from cookie
 * @returns The stored ttclid or null if not found
 */
export function getTtclid(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${TTCLID_KEY}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Captures ttclid from URL and stores it in cookie if present
 * Should be called on page load / pixel initialization
 * @param attributionDays - Optional: Override default attribution window
 */
export function captureTtclidFromUrl(attributionDays = DEFAULT_ATTRIBUTION_DAYS): void {
  const ttclidFromUrl = readTtclidFromUrl();
  if (ttclidFromUrl) {
    setTtclidCookie(ttclidFromUrl, attributionDays);
  }
}
